import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const MathCalculatorModal = ({ isOpen, onClose }) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleNumber = (num) => {
    if (waitingForOperand) {
      setDisplay(String(num));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(num) : display + num);
    }
  };

  const handleDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const handleOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = performOperation(currentValue, inputValue, operation);
      
      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const performOperation = (firstValue, secondValue, operation) => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return firstValue / secondValue;
      default:
        return secondValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const newValue = performOperation(previousValue, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const handleClear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const handleBackspace = () => {
    if (!waitingForOperand && display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content math-calculator-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="math-calc-title">
        <div className="modal-header">
          <h2 id="math-calc-title" className="modal-title">آلة حاسبة</h2>
          <button 
            ref={closeButtonRef}
            className="modal-close" 
            onClick={onClose}
            aria-label="إغلاق"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="calc-display">{display}</div>
          
          <div className="calc-buttons">
            <button className="calc-btn" onClick={handleClear}>C</button>
            <button className="calc-btn" onClick={handleBackspace}>⌫</button>
            <button className="calc-btn calc-operation" onClick={() => handleOperation('÷')}>÷</button>
            <button className="calc-btn calc-operation" onClick={() => handleOperation('×')}>×</button>
            
            <button className="calc-btn" onClick={() => handleNumber(7)}>7</button>
            <button className="calc-btn" onClick={() => handleNumber(8)}>8</button>
            <button className="calc-btn" onClick={() => handleNumber(9)}>9</button>
            <button className="calc-btn calc-operation" onClick={() => handleOperation('-')}>−</button>
            
            <button className="calc-btn" onClick={() => handleNumber(4)}>4</button>
            <button className="calc-btn" onClick={() => handleNumber(5)}>5</button>
            <button className="calc-btn" onClick={() => handleNumber(6)}>6</button>
            <button className="calc-btn calc-operation" onClick={() => handleOperation('+')}>+</button>
            
            <button className="calc-btn" onClick={() => handleNumber(1)}>1</button>
            <button className="calc-btn" onClick={() => handleNumber(2)}>2</button>
            <button className="calc-btn" onClick={() => handleNumber(3)}>3</button>
            <button className="calc-btn calc-equals" onClick={handleEquals}>=</button>
            
            <button className="calc-btn calc-zero" onClick={() => handleNumber(0)}>0</button>
            <button className="calc-btn" onClick={handleDecimal}>.</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathCalculatorModal;
